/**
 * Fill picker — "who could take this phase window?"
 *
 * Backs the "+ Assign" slot on an unfilled crew line, the "Fill from bench"
 * prompt a move leaves behind, and "Replace…" on a placed card. Candidates
 * come graded from `/RosterAssignments/suggest`; picking one writes a
 * manual assignment (or, in replace mode, hands the block over through the
 * caller's move) and the caller refetches.
 *
 * "Include people off shift (overtime)" re-queries with `includeOffShift`:
 * people on another of the plant's shifts that day come back as
 * `state: "overtime"` with the reason ("own shift 14:30-23:00; 8.5 h
 * overtime"); placing one asks first and saves the block as overtime. The
 * ask also lets the supervisor shorten the stay — a person held back after
 * their own shift usually covers part of the window, not all of it — so the
 * block saved is the hours chosen, clipped to the open slot.
 *
 * A candidate who is out (or asking to be) for that day wears the same badge
 * as the name chips, and sorts after the people who are simply free, so a
 * supervisor replacing someone who is out does not pick someone else who is.
 */
import CloseIcon from "@mui/icons-material/Close";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Switch,
} from "@mui/material";
import { addDays, differenceInMinutes, format, parseISO, set } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import rosterAssignmentsApi, { AvailabilityRow, SuggestCandidate } from "../../../../../services/roster-assignments-api";
import { apiErrorMessage } from "../../../../../utils/api-error";
import { toLocalIso } from "../../../utils/time";
import { availabilityBadge,AvailabilityIndex,EMPTY_AVAILABILITY } from "../utils/availability"; // eslint-disable-line sort-imports
import { eyebrowSx, fmtHours, lbb } from "../utils/tokens";
import { AvailabilityBadge, availabilityStripe, toneColor } from "./availability-badge";

export interface FillRequest {
	workOrderId: number
	phaseId?: number | null
	/** Absolute ISO window the assignment will cover. */
	start: string
	end: string
	jobRoleId?: number | null
	jobRoleName: string
	/** "75 CFT · Granulation · Operator" */
	title: string
	/** Set when the picker replaces a placed worker: choosing a candidate hands their block over. */
	replace?: { assignmentId: number; workerName: string; stepTitle: string }
}

interface FillPickerProps {
	request: FillRequest | null
	locationId: number
	onClose: () => void
	onAssigned: () => void | Promise<void>
	/** Replace mode: the caller moves the block to the chosen candidate (and closes the picker). */
	onReplace?: (assignmentId: number, candidate: SuggestCandidate) => void
	/** The week's time-off index, so a candidate who is out wears the badge here too. */
	availability?: AvailabilityIndex
}

interface GradedCandidate {
	candidate: SuggestCandidate
	/** The row that marks this person out / requesting for the window's day, if any. */
	marked: AvailabilityRow | null
}

/** Expected people first, then pending requests, then confirmed out. */
const markedRank = (marked: AvailabilityRow | null): number =>
	marked === null ? 0 : availabilityBadge(marked).tone === "warning" ? 1 : 2;

const stateKey = (state: string): string => state.toLowerCase();

const stateColor = (state: string): string => {
	const key = stateKey(state);
	if (key === "free" || key === "ok") {
		return lbb.success;
	}
	if (key === "busy" || key === "blocked") {
		return lbb.brick;
	}
	return lbb.warning;
};

/** free → overtime → busy → unqualified, then whatever else the server grades. */
const STATE_ORDER = ["free", "ok", "overtime", "busy", "blocked", "unqualified"];
const stateRank = (state: string): number => {
	const index = STATE_ORDER.indexOf(stateKey(state));
	return index === -1 ? STATE_ORDER.length : index;
};

const isOvertime = (candidate: SuggestCandidate): boolean => stateKey(candidate.state) === "overtime";
/** Holds the step's job (the server grades anyone without it "unqualified"). */
const hasJob = (candidate: SuggestCandidate): boolean => stateKey(candidate.state) !== "unqualified";
/**
 * Busy stays non-selectable. Someone without the job can be chosen when
 * replacing — the move asks "Place anyway?" first; a plain fill would be refused.
 */
const isSelectable = (candidate: SuggestCandidate, replacing: boolean): boolean => {
	const key = stateKey(candidate.state);
	return key === "free" || key === "ok" || key === "overtime" || (replacing && key === "unqualified");
};
/** Replacing someone, or asked for people not trained: grade the whole bench, so people without the job show (with a warning) too. */
const REPLACE_TAKE = 40;

/** Shortest overtime block the picker will save. */
const MIN_OVERTIME_MINUTES = 60;

/** "HH:mm" of a local ISO stamp. */
const clock = (iso: string): string => format(parseISO(iso), "HH:mm");

/**
 * The absolute window for a chosen start/end clock inside the slot: same day
 * as the slot's start; an end at or before the start means the next day.
 */
const windowFor = (slotStartIso: string, startClock: string, endClock: string): { start: Date; end: Date } => {
	const base = parseISO(slotStartIso);
	const at = (hhmm: string): Date => {
		const [h, m] = hhmm.split(":").map(Number);
		return set(base, { hours: h, minutes: m, seconds: 0, milliseconds: 0 });
	};
	const start = at(startClock);
	let end = at(endClock);
	if (end <= start) {
		end = addDays(end, 1);
	}
	return { start, end };
};

/** Why a chosen window cannot be saved, or null when it fits the slot. */
const windowProblem = (request: FillRequest, startClock: string, endClock: string): string | null => {
	const slotStart = parseISO(request.start);
	const slotEnd = parseISO(request.end);
	const { start, end } = windowFor(request.start, startClock, endClock);
	if (start < slotStart || end > slotEnd) {
		return `Stay within the open slot, ${clock(request.start)} – ${clock(request.end)}.`;
	}
	if (differenceInMinutes(end, start) < MIN_OVERTIME_MINUTES) {
		return "At least one hour.";
	}
	return null;
};

const fmtSpan = (minutes: number): string => {
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return m ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
};

const FillPicker: React.FC<FillPickerProps> = ({
	request,
	locationId,
	onClose,
	onAssigned,
	onReplace,
	availability = EMPTY_AVAILABILITY,
}) => {
	const [candidates, setCandidates] = useState<SuggestCandidate[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [includeOffShift, setIncludeOffShift] = useState(false);
	// "Include people not trained": the whole bench, people without the job asked about before placing.
	const [includeUntrained, setIncludeUntrained] = useState(false);
	const [untrainedAsk, setUntrainedAsk] = useState<SuggestCandidate | null>(null);
	const [overtimeAsk, setOvertimeAsk] = useState<SuggestCandidate | null>(null);
	// The hours an overtime placement covers; opens at the whole slot.
	const [otStart, setOtStart] = useState("");
	const [otEnd, setOtEnd] = useState("");

	// A new request starts from the on-shift bench.
	useEffect(() => {
		setIncludeOffShift(false);
		setIncludeUntrained(false);
		setUntrainedAsk(null);
		setOvertimeAsk(null);
	}, [request]);
	const grading = Boolean(request?.replace) || includeUntrained;

	useEffect(() => {
		if (!request) {
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError(null);
		setCandidates([]);
		rosterAssignmentsApi
			.suggest({
				workOrderId: request.workOrderId,
				phaseId: request.phaseId ?? undefined,
				start: request.start,
				end: request.end,
				locationId,
				includeOffShift: includeOffShift || undefined,
				take: request.replace || includeUntrained ? REPLACE_TAKE : undefined,
			})
			.then((result) => {
				if (!cancelled) {
					setCandidates(result.candidates ?? []);
				}
			})
			.catch((err) => {
				console.error("Failed to load suggestions:", err);
				if (!cancelled) {
					setError("Could not load the bench for this window.");
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [request, locationId, includeOffShift, includeUntrained]);

	// The server already orders free → overtime → busy → unqualified; sorting
	// here keeps that true for a server that does not. Within a state, people
	// who are out or asking to be sink below those simply expected.
	const ordered = useMemo<GradedCandidate[]>(() => {
		const day = request ? format(parseISO(request.start), "yyyy-MM-dd") : "";
		return candidates
			.map((candidate) => ({
				candidate,
				marked: request ? availability.lookup(candidate.workerId, candidate.shiftId, day) : null,
			}))
			.sort(
				(a, b) =>
					stateRank(a.candidate.state) - stateRank(b.candidate.state) ||
					markedRank(a.marked) - markedRank(b.marked) ||
					a.candidate.workerName.localeCompare(b.candidate.workerName)
			);
	}, [candidates, request, availability]);

	const place = useCallback(
		async (candidate: SuggestCandidate, overtime: boolean, window?: { start: Date; end: Date }) => {
			const untrained = !hasJob(candidate);
			const notes = [overtime ? "Overtime: outside own shift" : null, untrained ? `Not trained for ${request?.jobRoleName ?? "this job"}: placed by hand` : null]
				.filter(Boolean)
				.join(" · ");
			if (!request) {
				return;
			}
			if (request.replace && onReplace) {
				onReplace(request.replace.assignmentId, candidate);
				return;
			}
			setSaving(candidate.workerId);
			setError(null);
			try {
				await rosterAssignmentsApi.manualAssign({
					workOrderId: request.workOrderId,
					phaseId: request.phaseId ?? undefined,
					workerId: candidate.workerId,
					workerName: candidate.workerName,
					jobRoleId: request.jobRoleId ?? undefined,
					jobRoleName: request.jobRoleName,
					startTime: window ? toLocalIso(window.start) : request.start,
					endTime: window ? toLocalIso(window.end) : request.end,
					shiftId: candidate.shiftId ?? undefined,
					...(overtime ? { isOvertime: true } : {}),
					...(notes ? { notes } : {}),
				});
				await onAssigned();
				onClose();
			} catch (err) {
				setError(apiErrorMessage(err, "The assignment could not be saved."));
			} finally {
				setSaving(null);
			}
		},
		[request, onReplace, onAssigned, onClose]
	);

	/** After any "not trained" question: overtime asks for its hours, anything else is placed. */
	const placeChecked = useCallback(
		(candidate: SuggestCandidate) => {
			// In replace mode the move's own 409 ("Place as overtime?") asks — not twice.
			if (isOvertime(candidate) && !request?.replace) {
				if (request) {
					setOtStart(clock(request.start));
					setOtEnd(clock(request.end));
				}
				setOvertimeAsk(candidate);
				return;
			}
			void place(candidate, false);
		},
		[place, request]
	);
	const pick = useCallback(
		(candidate: SuggestCandidate) => {
			// Replacing asks through the move itself; a plain add asks here.
			if (!hasJob(candidate) && !request?.replace) {
				setUntrainedAsk(candidate);
				return;
			}
			placeChecked(candidate);
		},
		[placeChecked, request]
	);
	const cancelUntrained = useCallback(() => setUntrainedAsk(null), []);
	const confirmUntrained = useCallback(() => {
		if (!untrainedAsk) return;
		const candidate = untrainedAsk;
		setUntrainedAsk(null);
		placeChecked(candidate);
	}, [untrainedAsk, placeChecked]);
	const toggleUntrained = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setIncludeUntrained(event.target.checked), []);
	const cancelOvertime = useCallback(() => setOvertimeAsk(null), []);
	const otProblem = request && overtimeAsk ? windowProblem(request, otStart, otEnd) : null;
	const otMinutes = request && overtimeAsk && !otProblem ? differenceInMinutes(windowFor(request.start, otStart, otEnd).end, windowFor(request.start, otStart, otEnd).start) : 0;
	const otIsWholeSlot = Boolean(request && otStart === clock(request.start) && otEnd === clock(request.end));
	const confirmOvertime = useCallback(() => {
		if (overtimeAsk && request && !otProblem) {
			const candidate = overtimeAsk;
			setOvertimeAsk(null);
			void place(candidate, true, otIsWholeSlot ? undefined : windowFor(request.start, otStart, otEnd));
		}
	}, [overtimeAsk, request, otProblem, otIsWholeSlot, otStart, otEnd, place]);
	const changeOtStart = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setOtStart(event.target.value), []);
	const changeOtEnd = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setOtEnd(event.target.value), []);
	const toggleOffShift = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setIncludeOffShift(event.target.checked), []);

	const heading = request?.replace ? `Replace ${request.replace.workerName} on ${request.replace.stepTitle}` : "Fill from bench";

	return (
		<Dialog open={Boolean(request)} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, borderBottom: lbb.borderHair }}>
				<div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
					<span style={{ fontFamily: lbb.fontBrand, fontSize: 20, fontWeight: 700, color: lbb.navy }}>{heading}</span>
					{request && (
						<span style={{ fontFamily: lbb.fontMono, fontSize: 12, color: lbb.ink60 }}>
							{request.title} · {format(parseISO(request.start), "HH:mm")} – {format(parseISO(request.end), "HH:mm")}
						</span>
					)}
				</div>
				<IconButton size="small" onClick={onClose} aria-label="Close">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<div style={{ padding: "6px 24px", borderBottom: lbb.borderHair, background: lbb.alloyWarm }}>
					<FormControlLabel
						control={<Switch size="small" checked={includeOffShift} onChange={toggleOffShift} />}
						label="Include people off shift (overtime)"
						sx={{ "& .MuiFormControlLabel-label": { fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink } }}
					/>
					{!request?.replace && (
						<FormControlLabel
							control={<Switch size="small" checked={includeUntrained} onChange={toggleUntrained} />}
							label="Include people not trained for the job"
							sx={{ "& .MuiFormControlLabel-label": { fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink } }}
						/>
					)}
				</div>
				{loading && (
					<div style={{ padding: 32, display: "flex", justifyContent: "center" }}>
						<CircularProgress size={22} sx={{ color: lbb.brick }} />
					</div>
				)}
				{!loading && error && (
					<div style={{ padding: "16px 24px", fontFamily: lbb.fontUi, fontSize: 13, color: lbb.brickDeep }}>{error}</div>
				)}
				{!loading && !error && ordered.length === 0 && (
					<div style={{ padding: "24px", fontFamily: lbb.fontUi, fontSize: 13, color: lbb.ink60 }}>
						{includeOffShift ? "Nobody on any shift fits this window." : "Nobody on the bench fits this window."}
					</div>
				)}
				{!loading &&
					ordered.map(({ candidate, marked }) => {
						const overtime = isOvertime(candidate);
						const selectable = isSelectable(candidate, grading);
						const qualified = hasJob(candidate);
						const stripe = marked ? availabilityStripe(marked).borderLeft : overtime ? `3px solid ${lbb.warning}` : "3px solid transparent";
						return (
							<button
								key={candidate.workerId}
								type="button"
								disabled={saving !== null || !selectable}
								onClick={() => pick(candidate)}
								aria-disabled={!selectable}
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 90px 80px",
									alignItems: "center",
									gap: 12,
									width: "100%",
									padding: "10px 24px",
									border: "none",
									borderBottom: lbb.borderHair,
									borderLeft: stripe,
									background: saving === candidate.workerId ? lbb.alloyWarm : overtime ? "#FBF3E6" : lbb.paper,
									textAlign: "left",
									cursor: saving ? "progress" : selectable ? "pointer" : "not-allowed",
									opacity: selectable ? 1 : 0.6,
									// Replacing, or showing people not trained: who holds the job is green; who does not carries a warning.
									...(grading ? { background: saving === candidate.workerId ? lbb.alloyWarm : qualified ? lbb.successTint : "#FBF3E6" } : {}),
									font: "inherit",
								}}
							>
								<span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
									<span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
										<span style={{ fontFamily: lbb.fontUi, fontSize: 14, fontWeight: 600, color: grading && qualified ? lbb.success : lbb.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{candidate.workerName}
										</span>
										{grading && request && (
											<span
												title={qualified ? `Holds ${request.jobRoleName}` : `Does not hold ${request.jobRoleName} — you will be asked before placing`}
												style={{
													...eyebrowSx,
													fontSize: 9,
													padding: "1px 6px",
													borderRadius: 2,
													whiteSpace: "nowrap",
													...(qualified
														? { background: lbb.success, color: lbb.paper }
														: { border: `1px solid ${lbb.warning}`, color: lbb.warning }),
												}}
											>
												{qualified ? "✓ has the job" : "⚠ no job"}
											</span>
										)}
										{marked && <AvailabilityBadge row={marked} size={10} />}
										{overtime && (
											<span
												style={{
													...eyebrowSx,
													fontSize: 9,
													padding: "1px 6px",
													borderRadius: 2,
													background: lbb.warning,
													color: lbb.paper,
												}}
											>
												OT
											</span>
										)}
										{candidate.fromPlant && (
											<span
												title={`From ${candidate.fromPlant}, a plant of the same cluster`}
												style={{ ...eyebrowSx, fontSize: 9, padding: "1px 6px", borderRadius: 2, border: `1px dashed ${lbb.navy}`, color: lbb.navy }}
											>
												{candidate.fromPlant}
											</span>
										)}
									</span>
									{marked && (
										<span style={{ fontFamily: lbb.fontUi, fontSize: 12, color: toneColor(availabilityBadge(marked).tone) }}>
											{availabilityBadge(marked).tooltip}
										</span>
									)}
									{candidate.reason && (
										<span style={{ fontFamily: lbb.fontUi, fontSize: 12, color: overtime ? "#8A5A12" : lbb.ink60 }}>{candidate.reason}</span>
									)}
								</span>
								<span
									style={{
										fontFamily: lbb.fontMono,
										fontSize: 11,
										letterSpacing: "0.06em",
										textTransform: "uppercase",
										color: stateColor(candidate.state),
									}}
								>
									{candidate.state}
								</span>
								<span
									style={{
										fontFamily: lbb.fontMono,
										fontSize: 12,
										textAlign: "right",
										color: lbb.ink60,
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{fmtHours(candidate.hoursThatDay || 0)}
								</span>
							</button>
						);
					})}
			</DialogContent>

			{/* "Not trained — place anyway?" */}
			<Dialog open={Boolean(untrainedAsk)} onClose={cancelUntrained} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Place {untrainedAsk?.workerName} without the training?</DialogTitle>
				<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
					{untrainedAsk?.workerName} does not hold {request?.jobRoleName}. They go on the job for this shift only, noted as placed by hand without the training.
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelUntrained} autoFocus sx={{ color: lbb.ink60 }}>
						Cancel
					</Button>
					<Button onClick={confirmUntrained} variant="contained" sx={{ bgcolor: lbb.warning, "&:hover": { bgcolor: "#A86B14" } }}>
						Place anyway
					</Button>
				</DialogActions>
			</Dialog>

			{/* "Place as overtime?" */}
			<Dialog open={Boolean(overtimeAsk)} onClose={cancelOvertime} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Place {overtimeAsk?.workerName} as overtime?</DialogTitle>
				<DialogContent sx={{ fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
					<blockquote
						style={{
							margin: 0,
							padding: "8px 12px",
							borderLeft: `3px solid ${lbb.warning}`,
							background: lbb.alloyWarm,
						}}
					>
						{overtimeAsk?.reason || "Outside their own shift."}
					</blockquote>
					<div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
						<span style={{ ...eyebrowSx, color: lbb.ink60 }}>Hours they stay</span>
						<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
							<input
								type="time"
								step={900}
								value={otStart}
								onChange={changeOtStart}
								aria-label="Overtime starts"
								style={{ font: "inherit", fontFamily: lbb.fontMono, padding: "6px 8px", border: lbb.borderInk, borderRadius: 4 }}
							/>
							<span style={{ color: lbb.ink60 }}>–</span>
							<input
								type="time"
								step={900}
								value={otEnd}
								onChange={changeOtEnd}
								aria-label="Overtime ends"
								style={{ font: "inherit", fontFamily: lbb.fontMono, padding: "6px 8px", border: lbb.borderInk, borderRadius: 4 }}
							/>
							<span style={{ fontFamily: lbb.fontMono, fontSize: 13, color: otProblem ? lbb.brickDeep : lbb.ink60 }}>
								{otProblem ?? `${fmtSpan(otMinutes)}${otIsWholeSlot ? " · the whole slot" : " · the rest of the slot stays open"}`}
							</span>
						</div>
						{request && (
							<span style={{ fontSize: 12, color: lbb.ink60 }}>
								Open slot {clock(request.start)} – {clock(request.end)}. Shorten it when the person is staying back after their own shift.
							</span>
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelOvertime} autoFocus sx={{ color: lbb.ink60 }}>
						Cancel
					</Button>
					<Button
						onClick={confirmOvertime}
						disabled={Boolean(otProblem)}
						variant="contained"
						sx={{ bgcolor: lbb.warning, "&:hover": { bgcolor: "#A86B14" } }}
					>
						Place as overtime
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

export default FillPicker;
